import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { Subject, interval, of } from 'rxjs';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { WhatsappLiveChatService } from 'src/app/private/services/whatsapp-live-chat.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-whatsapp-live-chat',
  templateUrl: './whatsapp-live-chat.component.html',
  styleUrls: ['./whatsapp-live-chat.component.scss'],
})
export class WhatsappLiveChatComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('msgThread') msgThread!: ElementRef;

  private destroy$ = new Subject<void>();
  private shouldScroll = false;

  // ── Current user ──────────────────────────────────────────────────────────
  clientId: string = '';
  agentId: string = '';
  agentName: string = '';

  // ── Stats ─────────────────────────────────────────────────────────────────
  stats: any = { total: 0, bot: 0, agent: 0, open: 0, resolved: 0 };

  // ── Pending Live Chat sessions ────────────────────────────────────────────
  pendingLiveSessions: any[] = [];
  showPendingBanner = false;

  // ── Conversations list ────────────────────────────────────────────────────
  conversations: any[] = [];
  filteredConversations: any[] = [];
  convoLoading = false;
  activeFilter: string = 'all';
  searchQuery = '';

  // ── Selected conversation ─────────────────────────────────────────────────
  selectedConvo: any = null;
  messages: any[] = [];
  messagesLoading = false;

  // ── Send message ──────────────────────────────────────────────────────────
  newMessage = '';
  sendingMsg = false;

  // ── Action loading ────────────────────────────────────────────────────────
  takingOver = false;
  releasing = false;
  resolving = false;

  // ── Polling ───────────────────────────────────────────────────────────────
  private pollInterval = 8000; // 8 seconds

  readonly FILTERS = [
    { key: 'all', label: 'All', icon: '💬' },
    { key: 'open', label: 'Open', icon: '🟡' },
    { key: 'bot', label: 'Bot', icon: '🤖' },
    { key: 'agent', label: 'Live Agent', icon: '👤' },
    { key: 'resolved', label: 'Resolved', icon: '✅' },
  ];

  constructor(
    private waChatService: WhatsappLiveChatService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    this.clientId = this.localStorage.getClientId() || '';
    this.agentId = this.localStorage.getUserId() || '';
    this.agentName = this.localStorage.getProfileName() || '';

    this.loadStats();
    this.loadConversations();
    this.loadPendingLiveSessions();
    this.startPolling();
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Polling ───────────────────────────────────────────────────────────────
  private startPolling() {
    interval(this.pollInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadConversations(true);
        this.loadStats();
        this.loadPendingLiveSessions();
        if (this.selectedConvo) {
          this.loadMessages(this.selectedConvo, true);
        }
      });
  }

  // ── Load Stats ────────────────────────────────────────────────────────────
  loadStats() {
    this.waChatService
      .getWaConversationStats({ client_id: this.clientId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.stats = res?.stats || this.stats;
        },
        error: () => {},
      });
  }

  // ── Load Conversations ────────────────────────────────────────────────────
  loadConversations(silent = false) {
    if (!silent) this.convoLoading = true;

    const payload: any = { client_id: this.clientId, limit: 50 };
    if (this.activeFilter !== 'all') payload.status = this.activeFilter;

    this.waChatService
      .getWaConversations(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const newConvos = res?.conversations || [];
          const oldConvos = this.conversations || [];

          for (const nc of newConvos) {
            if (!nc.customerNumber || !nc.destinationNumber) {
              const old = oldConvos.find(
                (c: any) => c.conversationId === nc.conversationId,
              );
              if (old) {
                if (!nc.customerNumber && old.customerNumber)
                  nc.customerNumber = old.customerNumber;
                if (!nc.destinationNumber && old.destinationNumber)
                  nc.destinationNumber = old.destinationNumber;
                if (!nc.sourceNumber && old.sourceNumber)
                  nc.sourceNumber = old.sourceNumber;
              }
            }
          }

          this.conversations = newConvos;

          if (this.selectedConvo) {
            const updated = this.conversations.find(
              (c: any) =>
                c.conversationId === this.selectedConvo.conversationId,
            );
            if (updated) {
              if (!updated.customerNumber && this.selectedConvo.customerNumber)
                updated.customerNumber = this.selectedConvo.customerNumber;
              if (
                !updated.destinationNumber &&
                this.selectedConvo.destinationNumber
              )
                updated.destinationNumber =
                  this.selectedConvo.destinationNumber;
              if (!updated.sourceNumber && this.selectedConvo.sourceNumber)
                updated.sourceNumber = this.selectedConvo.sourceNumber;
              this.selectedConvo = { ...this.selectedConvo, ...updated };
            }
          }

          this.applySearch();
          this.convoLoading = false;
        },
        error: () => {
          this.convoLoading = false;
        },
      });
  }

  // ── Filter + Search ───────────────────────────────────────────────────────
  setFilter(key: string) {
    this.activeFilter = key;
    this.loadConversations();
  }

  applySearch() {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredConversations = [...this.conversations];
    } else {
      this.filteredConversations = this.conversations.filter(
        (c) =>
          c.customerNumber?.includes(q) ||
          c.message?.toLowerCase().includes(q) ||
          c.conversationId?.toLowerCase().includes(q),
      );
    }
  }

  // ── Select Conversation ───────────────────────────────────────────────────
  selectConversation(convo: any) {
    this.selectedConvo = convo;
    this.messages = [];
    this.newMessage = '';
    this.loadMessages(convo);
  }

  loadMessages(convo: any, silent = false) {
    if (!silent) this.messagesLoading = true;

    this.waChatService
      .getWaConversationMessages({
        client_id: this.clientId,
        conversationId: convo.conversationId,
        limit: 100,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const newMsgs: any[] = res?.messages || [];
          if (newMsgs.length !== this.messages.length) {
            const currentParsed = new Map(
              this.messages.map((m) => [m.id || m.createdAt, m.parsed]),
            );

            newMsgs.forEach((msg) => {
              const key = msg.id || msg.createdAt;
              if (currentParsed.has(key)) {
                msg.parsed = currentParsed.get(key);
              } else {
                msg.parsed = this.parseMessage(msg.message);
              }
            });
            this.messages = newMsgs;
            this.shouldScroll = true;
          }

          if (res?.session && this.selectedConvo) {
            const sess = res.session;
            this.selectedConvo.chat_type = sess.chat_type;
            this.selectedConvo.session_status = sess.session_status;
            this.selectedConvo.is_live_agent = sess.is_live_agent;
            this.selectedConvo.assigned_agent_id = sess.assigned_agent_id;
            this.selectedConvo.assigned_agent_name = sess.assigned_agent_name;
            this.selectedConvo.taken_over_at = sess.taken_over_at;
            if (sess.source_number) {
              this.selectedConvo.sourceNumber = sess.source_number;
            }

            this.selectedConvo.agentMode = sess.is_live_agent;
            this.selectedConvo.status = sess.is_live_agent ? 'agent' : 'bot';
            this.selectedConvo.assignedAgentId = sess.assigned_agent_id;
            this.selectedConvo.assignedAgentName = sess.assigned_agent_name;
          }

          this.messagesLoading = false;
        },
        error: () => {
          this.messagesLoading = false;
        },
      });
  }

  private scrollToBottom() {
    try {
      const el = this.msgThread?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }

  // ── Takeover ──────────────────────────────────────────────────────────────
  takeover() {
    if (!this.selectedConvo) return;
    this.takingOver = true;

    this.waChatService
      .takeoverConversation({
        client_id: this.clientId,
        conversationId: this.selectedConvo.conversationId,
        agent_id: this.agentId,
        agent_name: this.agentName,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.takingOver = false;
          this.selectedConvo.agentMode = true;
          this.selectedConvo.status = 'agent';
          this.selectedConvo.assignedAgentId = this.agentId;
          this.selectedConvo.assignedAgentName = this.agentName;

          this.selectedConvo.is_live_agent = true;
          this.selectedConvo.session_status = 'active';
          this.selectedConvo.assigned_agent_id = this.agentId;
          this.selectedConvo.assigned_agent_name = this.agentName;

          this.toastr.success(
            'You are now the live agent for this conversation',
          );
          this.sendNotificationMessage(
            `You are now chatting with live agent ${this.agentName}.`,
          );
          this.loadMessages(this.selectedConvo);
          this.loadStats();
          this.loadPendingLiveSessions();
        },
        error: () => {
          this.takingOver = false;
          this.toastr.error('Failed to take over conversation');
        },
      });
  }

  // ── Release ───────────────────────────────────────────────────────────────
  release() {
    if (!this.selectedConvo) return;
    this.releasing = true;

    this.waChatService
      .sendWaAgentMessage({
        client_id: this.clientId,
        conversationId: this.selectedConvo.conversationId,
        agent_id: this.agentId,
        agent_name: this.agentName,
        customerNumber: this.selectedConvo.customerNumber,
        sourceNumber: this.selectedConvo.sourceNumber,
        message: 'You are now chatting with the bot.',
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
        switchMap(() => {
          return this.waChatService.releaseConversation({
            client_id: this.clientId,
            conversationId: this.selectedConvo.conversationId,
            agent_id: this.agentId,
            agent_name: this.agentName,
          });
        }),
      )
      .subscribe({
        next: () => {
          this.releasing = false;
          this.selectedConvo.agentMode = false;
          this.selectedConvo.status = 'bot';
          this.selectedConvo.assignedAgentId = '';

          this.selectedConvo.is_live_agent = false;
          this.selectedConvo.session_status = 'closed';
          this.selectedConvo.assigned_agent_id = '';
          this.selectedConvo.assigned_agent_name = '';

          this.toastr.info('Conversation released back to bot');
          this.loadMessages(this.selectedConvo);
          this.loadStats();
          this.loadPendingLiveSessions();
        },
        error: () => {
          this.releasing = false;
          this.toastr.error('Failed to release conversation');
        },
      });
  }

  // ── Resolve ───────────────────────────────────────────────────────────────
  resolve() {
    if (!this.selectedConvo) return;
    this.resolving = true;

    this.waChatService
      .resolveConversation({
        client_id: this.clientId,
        conversationId: this.selectedConvo.conversationId,
        agent_id: this.agentId,
        agent_name: this.agentName,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.resolving = false;
          this.selectedConvo.status = 'resolved';
          this.selectedConvo.agentMode = false;

          this.selectedConvo.session_status = 'closed';
          this.selectedConvo.is_live_agent = false;

          this.toastr.success('Conversation marked as resolved');
          this.loadMessages(this.selectedConvo);
          this.loadConversations();
          this.loadStats();
          this.loadPendingLiveSessions();
        },
        error: () => {
          this.resolving = false;
          this.toastr.error('Failed to resolve conversation');
        },
      });
  }

  // ── Send Message ──────────────────────────────────────────────────────────
  sendMessage() {
    const text = this.newMessage.trim();
    if (!text || !this.selectedConvo?.agentMode || this.sendingMsg) return;

    this.sendingMsg = true;

    this.waChatService
      .sendWaAgentMessage({
        client_id: this.clientId,
        conversationId: this.selectedConvo.conversationId,
        agent_id: this.agentId,
        agent_name: this.agentName,
        customerNumber: this.selectedConvo.customerNumber,
        sourceNumber: this.selectedConvo.sourceNumber,
        message: text,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendingMsg = false;
          this.newMessage = '';
          this.loadMessages(this.selectedConvo);
        },
        error: () => {
          this.sendingMsg = false;
          this.toastr.error('Failed to send message');
        },
      });
  }

  // ── Send Notification Message ─────────────────────────────────────────────
  sendNotificationMessage(text: string) {
    if (!this.selectedConvo) return;

    this.waChatService
      .sendWaAgentMessage({
        client_id: this.clientId,
        conversationId: this.selectedConvo.conversationId,
        agent_id: this.agentId,
        agent_name: this.agentName,
        customerNumber: this.selectedConvo.customerNumber,
        sourceNumber: this.selectedConvo.sourceNumber,
        message: text,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadMessages(this.selectedConvo);
        },
        error: (err) => {
          console.error('Failed to send notification message', err);
        },
      });
  }

  onEnter(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  getStatusBadge(status: string): { label: string; cls: string } {
    const st = status || 'bot';
    const map: any = {
      bot: { label: '🤖 Bot', cls: 'status-bot' },
      agent: { label: '👤 Agent', cls: 'status-agent' },
      open: { label: '🟡 Open', cls: 'status-open' },
      resolved: { label: '✅ Resolved', cls: 'status-resolved' },
    };
    return map[st] || { label: st, cls: 'status-open' };
  }

  getMsgClass(msg: any): string {
    if (msg.senderType === 'system') return 'msg-system';
    if (msg.senderType === 'agent') return 'msg-agent';
    if (msg.senderType === 'bot') return 'msg-bot';
    return 'msg-customer'; // Fallback ensures user is always left-aligned
  }

  getMsgLabel(msg: any): string {
    if (msg.senderType === 'system') return '⚙ System';
    if (msg.senderType === 'agent') return `👤 ${msg.senderName || 'Agent'}`;
    if (msg.senderType === 'bot') return `🤖 ${msg.botName || 'Bot'}`;
    return `📱 ${msg.customerNumber || 'Customer'}`;
  }

  formatTime(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDate(iso: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  getInitial(name: any): string {
    const str = String(name || 'C');
    return str.charAt(0).toUpperCase();
  }

  playNotificationSound() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, now); // D5
      gain1.gain.setValueAtTime(0.15, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.35);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now + 0.12); // A5
      gain2.gain.setValueAtTime(0.15, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.5);
    } catch (e) {
      console.warn('Audio play blocked or unsupported:', e);
    }
  }

  avatarColor(seed: any): string {
    const str = String(seed || '');
    const palette = [
      '#0d4259',
      '#1a6a8a',
      '#f0950c',
      '#c97a08',
      '#6c63ff',
      '#34B7F1',
    ];
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = str.charCodeAt(i) + ((h << 5) - h);
    }
    return palette[Math.abs(h) % palette.length];
  }

  isAgentOfConvo(convo: any): boolean {
    return (
      convo?.agentMode &&
      String(convo?.assignedAgentId) === String(this.agentId)
    );
  }

  canSend(): boolean {
    return (
      !!this.selectedConvo?.agentMode &&
      !!this.newMessage.trim() &&
      !this.sendingMsg &&
      this.selectedConvo?.status !== 'resolved'
    );
  }

  parseMessage(message: string): any {
    if (!message) return { text: [] };
    let textStr = message;
    let videoLink: SafeResourceUrl | null = null;
    let videoTitle: string | null = null;

    const videoLinkMatch = textStr.match(/#videoLink='([^']+)'/);
    if (videoLinkMatch) {
      videoLink = this.sanitizer.bypassSecurityTrustResourceUrl(
        videoLinkMatch[1],
      );
      textStr = textStr.replace(videoLinkMatch[0], '');
    }

    const videoTitleMatch = textStr.match(/videoTitle='([^']+)'/);
    if (videoTitleMatch) {
      videoTitle = videoTitleMatch[1];
      textStr = textStr.replace(videoTitleMatch[0], '');
    }

    let textArray: string[] = [];
    if (textStr.includes('~')) {
      textArray = textStr
        .split('~')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    } else {
      textArray = textStr
        .split('\n')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    }

    return { videoLink, videoTitle, text: textArray };
  }

  trackByMsgId(index: number, msg: any) {
    return msg.id || msg.createdAt || index;
  }

  trackByConvoId(index: number, convo: any) {
    return convo.conversationId || index;
  }

  // ── Load Pending Live Sessions ────────────────────────────────────────────
  loadPendingLiveSessions() {
    if (!this.clientId) return;
    this.waChatService
      .getPendingLiveSessions({ client_id: this.clientId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const allSessions = res?.sessions || [];
          const newSessions = allSessions.filter(
            (s: any) => s.session_status === 'pending' || !s.session_status,
          );
          if (newSessions.length > this.pendingLiveSessions.length) {
            this.showPendingBanner = true;
            this.playNotificationSound();
          } else if (newSessions.length === 0) {
            this.showPendingBanner = false;
          }
          this.pendingLiveSessions = newSessions;
        },
        error: () => {},
      });
  }

  // ── Jump to Pending Session ───────────────────────────────────────────────
  jumpToPendingSession(session: any) {
    const convoId = session.conversationId || session.conversation_id;
    if (!convoId) return;

    let convo = this.conversations.find(
      (c: any) => c.conversationId === convoId,
    );
    if (convo) {
      this.selectConversation(convo);
    } else {
      this.activeFilter = 'all';
      this.convoLoading = true;
      const payload: any = { client_id: this.clientId, limit: 50 };
      this.waChatService
        .getWaConversations(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.conversations = res?.conversations || [];
            this.applySearch();
            this.convoLoading = false;

            const found = this.conversations.find(
              (c: any) => c.conversationId === convoId,
            );
            if (found) {
              this.selectConversation(found);
            } else {
              this.toastr.warning('Conversation not found in list');
            }
          },
          error: () => {
            this.convoLoading = false;
            this.toastr.error('Failed to load conversations');
          },
        });
    }
  }

  // ── Get Conversation Status Label ─────────────────────────────────────────
  getConvoStatusLabel(convo: any): { label: string; cls: string } {
    if (convo?.is_live_agent) {
      const sessStatus = convo.session_status || 'pending';
      if (sessStatus === 'pending') {
        return { label: '⏳ Waiting', cls: 'status-pending' };
      }
      if (sessStatus === 'active') {
        return { label: '👤 Live', cls: 'status-agent' };
      }
      if (sessStatus === 'closed') {
        return { label: '✅ Closed', cls: 'status-resolved' };
      }
    }
    const status = convo?.status || 'bot';
    if (status === 'resolved' || convo?.session_status === 'closed') {
      return { label: '✅ Closed', cls: 'status-resolved' };
    }
    if (status === 'agent' || convo?.session_status === 'active') {
      return { label: '👤 Live', cls: 'status-agent' };
    }
    return { label: '🤖 Bot', cls: 'status-bot' };
  }

  // ── Get Session Status Label ──────────────────────────────────────────────
  getSessionStatusLabel(): { label: string; cls: string } {
    if (!this.selectedConvo) return { label: 'Unknown', cls: 'status-open' };
    return this.getConvoStatusLabel(this.selectedConvo);
  }

  // ── Is Pending Live Chat ──────────────────────────────────────────────────
  isPendingLiveChat(): boolean {
    return (
      !!this.selectedConvo &&
      !!this.selectedConvo.is_live_agent &&
      (this.selectedConvo.session_status === 'pending' || !this.selectedConvo.session_status)
    );
  }

  // ── Is Active Agent Conversation ──────────────────────────────────────────
  isActiveAgentConvo(): boolean {
    if (!this.selectedConvo) return false;
    const isLiveAgentActive =
      !!this.selectedConvo.is_live_agent &&
      this.selectedConvo.session_status === 'active' &&
      (String(this.selectedConvo.assigned_agent_id) === String(this.agentId) ||
        String(this.selectedConvo.assignedAgentId) === String(this.agentId));
    const isAgentMode =
      !!this.selectedConvo.agentMode &&
      String(this.selectedConvo.assignedAgentId) === String(this.agentId);
    return isLiveAgentActive || isAgentMode;
  }
}
